import React, { useState } from 'react';
import { PageHeader } from '../../components/layout/PageHeader';
import { KPICard } from '../../components/ui/KPICard';
import { Card } from '../../components/ui/Card';
import { DataTable } from '../../components/ui/DataTable';
import { Tabs } from '../../components/ui/Tabs';
import { Button } from '../../components/ui/Button';
import { StatusPill } from '../../components/ui/StatusPill';
import { Drawer } from '../../components/ui/Drawer';
import { FileSignatureIcon, ClockIcon, AlertCircleIcon } from 'lucide-react';
import { contracts } from '../../data/contracts';
export const ContractConsolePage: React.FC = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [selectedContract, setSelectedContract] = useState<any>(null);
  return <div className="max-w-7xl mx-auto space-y-6" data-id="element-341">
      <PageHeader title="Contract Console" description="Manage contract drafting, redlining, and execution." primaryAction={<Button data-id="element-343">Draft New Contract</Button>} data-id="element-342" />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4" data-id="element-344">
        <KPICard title="Active Contracts" value="6" icon={<FileSignatureIcon className="w-5 h-5" data-id="element-346" />} data-id="element-345" />
        <KPICard title="In Negotiation" value="1" icon={<ClockIcon className="w-5 h-5 text-status-warning" data-id="element-348" />} data-id="element-347" />
        <KPICard title="Pending Signature" value="1" icon={<FileSignatureIcon className="w-5 h-5 text-status-info" data-id="element-350" />} data-id="element-349" />
        <KPICard title="Expiring < 90 Days" value="1" icon={<AlertCircleIcon className="w-5 h-5 text-status-error" data-id="element-352" />} data-id="element-351" />
      </div>

      <Card noPadding data-id="element-353">
        <div className="px-6 pt-4 border-b border-border-light" data-id="element-354">
          <Tabs tabs={[{
          id: 'all',
          label: 'All Contracts'
        }, {
          id: 'drafting',
          label: 'Drafting'
        }, {
          id: 'negotiation',
          label: 'Negotiation'
        }, {
          id: 'signature',
          label: 'Pending Signature'
        }, {
          id: 'active',
          label: 'Active'
        }]} activeTab={activeTab} onChange={setActiveTab} data-id="element-355" />
        </div>
        <div className="p-6" data-id="element-356">
          <DataTable data={contracts} columns={[{
          header: 'Contract ID',
          accessorKey: 'id',
          cell: row => <span className="font-medium text-fpg-navy" data-id="element-358">{row.id}</span>
        }, {
          header: 'Title',
          accessorKey: 'title'
        }, {
          header: 'Supplier',
          accessorKey: 'supplierName'
        }, {
          header: 'Type',
          accessorKey: 'type'
        }, {
          header: 'Value',
          cell: row => `$${(row.value / 1000000).toFixed(1)}M`
        }, {
          header: 'Status',
          cell: row => <StatusPill status={row.status} data-id="element-359" />
        }]} onRowClick={setSelectedContract} data-id="element-357" />
        </div>
      </Card>

      <Drawer isOpen={!!selectedContract} onClose={() => setSelectedContract(null)} title="Contract Details" size="lg" footer={<>
            <Button variant="ghost" onClick={() => setSelectedContract(null)} data-id="element-361">
              Close
            </Button>
            <Button variant="secondary" data-id="element-362">Request Changes</Button>
            <Button variant="primary" data-id="element-363">Approve</Button>
          </>} data-id="element-360">
        {selectedContract && <div className="space-y-6" data-id="element-364">
            <div className="flex justify-between items-start" data-id="element-365">
              <div data-id="element-366">
                <h2 className="text-xl font-bold text-text-primary mb-1" data-id="element-367">
                  {selectedContract.title}
                </h2>
                <p className="text-sm text-text-secondary" data-id="element-368">
                  {selectedContract.supplierName} • {selectedContract.id}
                </p>
              </div>
              <StatusPill status={selectedContract.status} data-id="element-369" />
            </div>

            <div className="grid grid-cols-2 gap-4" data-id="element-370">
              <div className="p-4 bg-surface-secondary rounded-fluent border border-border-light" data-id="element-371">
                <p className="text-sm text-text-secondary mb-1" data-id="element-372">Term</p>
                <p className="font-medium" data-id="element-373">
                  {selectedContract.startDate} to {selectedContract.endDate}
                </p>
              </div>
              <div className="p-4 bg-surface-secondary rounded-fluent border border-border-light" data-id="element-374">
                <p className="text-sm text-text-secondary mb-1" data-id="element-375">Total Value</p>
                <p className="font-medium" data-id="element-376">
                  ${(selectedContract.value / 1000000).toFixed(1)}M{' '}
                  {selectedContract.currency}
                </p>
              </div>
            </div>

            {selectedContract.clauseDeviations > 0 && <div data-id="element-377">
                <h3 className="text-lg font-semibold mb-3 flex items-center gap-2" data-id="element-378">
                  <AlertCircleIcon className="w-5 h-5 text-status-warning" data-id="element-379" />{' '}
                  Clause Deviations ({selectedContract.clauseDeviations})
                </h3>
                <div className="border border-border-light rounded-fluent overflow-hidden" data-id="element-380">
                  <table className="w-full text-sm text-left" data-id="element-381">
                    <thead className="bg-surface-secondary border-b border-border-light" data-id="element-382">
                      <tr data-id="element-383">
                        <th className="p-2 font-medium" data-id="element-384">Clause</th>
                        <th className="p-2 font-medium" data-id="element-385">Standard Text</th>
                        <th className="p-2 font-medium text-status-error" data-id="element-386">
                          Proposed Redline
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-light" data-id="element-387">
                      <tr data-id="element-388">
                        <td className="p-2 font-medium" data-id="element-389">7.2 Liability Cap</td>
                        <td className="p-2 text-text-secondary" data-id="element-390">
                          Liability capped at 2x contract value.
                        </td>
                        <td className="p-2" data-id="element-391">
                          Liability capped at 1x contract value.
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>}

            <div data-id="element-392">
              <h3 className="text-lg font-semibold mb-3" data-id="element-393">
                Signature Readiness
              </h3>
              <ul className="space-y-2 text-sm" data-id="element-394">
                <li className="flex items-center gap-2" data-id="element-395">
                  <CheckCircleIcon className="w-4 h-4 text-status-success" data-id="element-396" />{' '}
                  Business Owner Approval: Complete
                </li>
                <li className="flex items-center gap-2" data-id="element-397">
                  <CheckCircleIcon className="w-4 h-4 text-status-success" data-id="element-398" />{' '}
                  Finance Approval: Complete
                </li>
                <li className="flex items-center gap-2" data-id="element-399">
                  <ClockIcon className="w-4 h-4 text-status-warning" data-id="element-400" /> Legal
                  Review: Pending
                </li>
              </ul>
            </div>
          </div>}
      </Drawer>
    </div>;
};